/**
 * Admin Settings Page - Olympic Fit Wellness
 * Connected to live API for site settings management
 */
import { useState, useEffect } from 'react';
import { 
  Settings, 
  Globe,
  Mail,
  Bell,
  Shield,
  Database,
  Save,
  Clock,
  MapPin,
  Phone,
  Loader2,
  AlertCircle,
  Check,
  X
} from 'lucide-react';
import api from '../../lib/api';

export default function AdminSettingsPage() {
  const [activeTab, setActiveTab] = useState('general');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [settings, setSettings] = useState({
    site_name: 'Olympic Fit Wellness',
    site_description: 'A comprehensive wellness center',
    contact_email: 'info@olympicfitwellness.com',
    contact_phone: '(469) 585-1023',
    address: '2601 E State Hwy 121, Building 1 Suite 114',
    city: 'Lewisville',
    state: 'TX',
    zip_code: '75056',
    business_hours: {
      monday: { open: '6:00 AM', close: '8:00 PM' },
      tuesday: { open: '6:00 AM', close: '8:00 PM' },
      wednesday: { open: '6:00 AM', close: '8:00 PM' },
      thursday: { open: '6:00 AM', close: '8:00 PM' },
      friday: { open: '6:00 AM', close: '3:00 PM' },
      saturday: { open: '8:00 AM', close: '3:00 PM' },
      sunday: { open: '1:00 PM', close: '4:00 PM' }
    },
    booking_advance_days: 7,
    cancel_hours_required: 4,
    max_bookings_per_day: 3,
    social_links: {
      facebook: '',
      instagram: '',
      twitter: '',
      youtube: ''
    }
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const data = await api.getSiteSettings();
      if (data) {
        setSettings(prev => ({
          ...prev,
          ...data,
          business_hours: data.business_hours || prev.business_hours,
          social_links: data.social_links || prev.social_links
        }));
      }
    } catch (err) {
      console.error('Error loading settings:', err);
      // Keep default settings on error
    } finally {
      setIsLoading(false);
    }
  };

  const tabs = [
    { id: 'general', name: 'General', icon: Globe },
    { id: 'location', name: 'Location', icon: MapPin },
    { id: 'hours', name: 'Business Hours', icon: Clock },
    { id: 'booking', name: 'Booking Rules', icon: Database },
    { id: 'social', name: 'Social Links', icon: Bell }
  ];

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await api.updateSiteSettings(settings);
      setSuccess('Settings saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Save error:', err);
      setError(err.message || 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const updateHours = (day, field, value) => {
    setSettings(prev => ({
      ...prev,
      business_hours: {
        ...prev.business_hours,
        [day]: {
          ...prev.business_hours[day],
          [field]: value
        }
      }
    }));
  };

  const updateSocialLink = (platform, value) => {
    setSettings(prev => ({
      ...prev,
      social_links: {
        ...prev.social_links,
        [platform]: value
      }
    }));
  };

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#3B82F6]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-settings">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-white">Site Settings</h1>
          <p className="text-neutral-400">Configure business information and system settings</p>
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="inline-flex items-center justify-center gap-2 bg-[#3B82F6] text-white font-semibold px-6 py-3 text-sm tracking-wider hover:bg-blue-600 transition-colors disabled:opacity-50"
          data-testid="save-settings-btn"
        >
          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {isSaving ? 'SAVING...' : 'SAVE CHANGES'}
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}
      {success && (
        <div className="bg-green-500/20 border border-green-500/50 text-green-400 px-4 py-3 rounded-lg flex items-center gap-2">
          <Check className="w-5 h-5" />
          {success}
        </div>
      )}

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="bg-neutral-800 rounded-xl border border-neutral-700 p-4">
          <nav className="space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-[#3B82F6] text-white'
                    : 'text-neutral-400 hover:bg-neutral-700 hover:text-white'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                <span className="font-medium">{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="lg:col-span-3 bg-neutral-800 rounded-xl border border-neutral-700">
          {activeTab === 'general' && (
            <>
              <div className="p-6 border-b border-neutral-700">
                <h2 className="text-xl font-heading font-bold text-white">General Settings</h2>
                <p className="text-neutral-400 text-sm">Basic site configuration</p>
              </div>
              
              <div className="p-6 space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-neutral-300 mb-2">Site Name</label>
                    <input
                      type="text"
                      value={settings.site_name}
                      onChange={(e) => setSettings({...settings, site_name: e.target.value})}
                      className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white focus:outline-none focus:border-[#3B82F6]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-300 mb-2">Contact Email</label>
                    <input
                      type="email"
                      value={settings.contact_email}
                      onChange={(e) => setSettings({...settings, contact_email: e.target.value})}
                      className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white focus:outline-none focus:border-[#3B82F6]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-300 mb-2">Contact Phone</label>
                    <input
                      type="tel"
                      value={settings.contact_phone}
                      onChange={(e) => setSettings({...settings, contact_phone: e.target.value})}
                      className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white focus:outline-none focus:border-[#3B82F6]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-300 mb-2">Site Description</label>
                    <input
                      type="text"
                      value={settings.site_description}
                      onChange={(e) => setSettings({...settings, site_description: e.target.value})}
                      className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white focus:outline-none focus:border-[#3B82F6]"
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === 'location' && (
            <>
              <div className="p-6 border-b border-neutral-700">
                <h2 className="text-xl font-heading font-bold text-white">Location Settings</h2>
                <p className="text-neutral-400 text-sm">Business address configuration</p>
              </div>
              
              <div className="p-6 space-y-6">
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">Street Address</label>
                  <input
                    type="text"
                    value={settings.address}
                    onChange={(e) => setSettings({...settings, address: e.target.value})}
                    className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white focus:outline-none focus:border-[#3B82F6]"
                  />
                </div>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-neutral-300 mb-2">City</label>
                    <input
                      type="text"
                      value={settings.city}
                      onChange={(e) => setSettings({...settings, city: e.target.value})}
                      className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white focus:outline-none focus:border-[#3B82F6]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-300 mb-2">State</label>
                    <input
                      type="text"
                      value={settings.state}
                      onChange={(e) => setSettings({...settings, state: e.target.value})}
                      className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white focus:outline-none focus:border-[#3B82F6]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-300 mb-2">ZIP Code</label>
                    <input
                      type="text"
                      value={settings.zip_code}
                      onChange={(e) => setSettings({...settings, zip_code: e.target.value})}
                      className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white focus:outline-none focus:border-[#3B82F6]"
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === 'hours' && (
            <>
              <div className="p-6 border-b border-neutral-700">
                <h2 className="text-xl font-heading font-bold text-white">Business Hours</h2>
                <p className="text-neutral-400 text-sm">Set operating hours for each day</p>
              </div>
              
              <div className="p-6 space-y-4">
                {days.map((day) => (
                  <div key={day} className="flex items-center gap-4 p-4 bg-neutral-700/50 rounded-lg">
                    <span className="w-28 text-white font-medium capitalize">{day}</span>
                    <div className="flex items-center gap-2 flex-1">
                      <input
                        type="text"
                        value={settings.business_hours[day]?.open || ''}
                        onChange={(e) => updateHours(day, 'open', e.target.value)}
                        placeholder="Opening time"
                        className="flex-1 px-3 py-2 bg-neutral-700 border border-neutral-600 rounded-lg text-white text-sm focus:outline-none focus:border-[#3B82F6]"
                      />
                      <span className="text-neutral-400">to</span>
                      <input
                        type="text"
                        value={settings.business_hours[day]?.close || ''}
                        onChange={(e) => updateHours(day, 'close', e.target.value)}
                        placeholder="Closing time"
                        className="flex-1 px-3 py-2 bg-neutral-700 border border-neutral-600 rounded-lg text-white text-sm focus:outline-none focus:border-[#3B82F6]"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {activeTab === 'booking' && (
            <>
              <div className="p-6 border-b border-neutral-700">
                <h2 className="text-xl font-heading font-bold text-white">Booking Rules</h2>
                <p className="text-neutral-400 text-sm">Configure booking restrictions</p>
              </div>
              
              <div className="p-6 space-y-6">
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-neutral-300 mb-2">Advance Booking Days</label>
                    <input
                      type="number"
                      value={settings.booking_advance_days}
                      onChange={(e) => setSettings({...settings, booking_advance_days: parseInt(e.target.value) || 0})}
                      className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white focus:outline-none focus:border-[#3B82F6]"
                    />
                    <p className="text-xs text-neutral-500 mt-1">How far in advance members can book</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-300 mb-2">Cancel Hours Required</label>
                    <input
                      type="number"
                      value={settings.cancel_hours_required}
                      onChange={(e) => setSettings({...settings, cancel_hours_required: parseInt(e.target.value) || 0})}
                      className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white focus:outline-none focus:border-[#3B82F6]"
                    />
                    <p className="text-xs text-neutral-500 mt-1">Minimum hours before class for cancellation</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-300 mb-2">Max Bookings/Day</label>
                    <input
                      type="number"
                      value={settings.max_bookings_per_day}
                      onChange={(e) => setSettings({...settings, max_bookings_per_day: parseInt(e.target.value) || 0})}
                      className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white focus:outline-none focus:border-[#3B82F6]"
                    />
                    <p className="text-xs text-neutral-500 mt-1">Maximum classes a member can book per day</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === 'social' && (
            <>
              <div className="p-6 border-b border-neutral-700">
                <h2 className="text-xl font-heading font-bold text-white">Social Media Links</h2>
                <p className="text-neutral-400 text-sm">Connect your social media profiles</p>
              </div>
              
              <div className="p-6 space-y-4">
                {['facebook', 'instagram', 'twitter', 'youtube'].map((platform) => (
                  <div key={platform}>
                    <label className="block text-sm font-medium text-neutral-300 mb-2 capitalize">{platform}</label>
                    <input
                      type="url"
                      value={settings.social_links?.[platform] || ''}
                      onChange={(e) => updateSocialLink(platform, e.target.value)}
                      placeholder={`https://${platform}.com/yourpage`}
                      className="w-full px-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white focus:outline-none focus:border-[#3B82F6]"
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
